import { useState, useEffect, useCallback } from 'react';

export const FORCE_HIDE_FORMS_PANEL = true;
const FORMS_PANEL_WIDTH = 0;

// 동적 패널 설정 계산 함수
const calculateDefaultPanelConfig = () => {
  const screenWidth = window.innerWidth || 1920; // 기본값 1920px
  const dashboardWidth = Math.floor(screenWidth * 0.75); // 화면의 75%
  
  return {
    dashboard: {
      visible: true,
      width: dashboardWidth,
      minWidth: 200,
      maxWidth: 2000
    },
    chat: {
      visible: true,
      width: 450, // 픽셀
      minWidth: 200,
      maxWidth: 2500
    },
    forms: {
      visible: false, // 기본으로 숨김 처리
      width: FORMS_PANEL_WIDTH, // 강제 숨김 시 0
      minWidth: FORMS_PANEL_WIDTH,
      maxWidth: FORMS_PANEL_WIDTH
    }
  };
};

// 기본 패널 설정 (동적 계산)
const DEFAULT_PANEL_CONFIG = calculateDefaultPanelConfig();

// localStorage 키
const STORAGE_KEY = 'panelLayout';

export const usePanelLayout = () => {
  // 패널 설정 상태
  const [panelConfig, setPanelConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const savedConfig = saved ? JSON.parse(saved) : {};
      
      // 현재 화면 크기 기준으로 기본값 재계산
      const currentDefaultConfig = calculateDefaultPanelConfig();
      
      // 설정 검증 및 수정
      const validatedConfig = {};
      for (const [panelName, defaultConfig] of Object.entries(currentDefaultConfig)) {
        const savedPanel = savedConfig[panelName] || {};
        validatedConfig[panelName] = {
          visible: savedPanel.visible !== undefined ? savedPanel.visible : defaultConfig.visible,
          width: savedPanel.width || defaultConfig.width,
          minWidth: defaultConfig.minWidth, // 항상 기본값 사용
          maxWidth: defaultConfig.maxWidth  // 항상 기본값 사용
        };
        
        // 최소/최대 크기 검증
        if (validatedConfig[panelName].minWidth > validatedConfig[panelName].maxWidth) {
          validatedConfig[panelName] = { ...defaultConfig };
        }
      }

      // 폼 패널 강제 숨김 적용 (저장된 설정 무시)
      if (FORCE_HIDE_FORMS_PANEL) {
        validatedConfig.forms = {
          visible: false,
          width: FORMS_PANEL_WIDTH,
          minWidth: FORMS_PANEL_WIDTH,
          maxWidth: FORMS_PANEL_WIDTH
        };
      }

      return validatedConfig;
    } catch (error) {
      return calculateDefaultPanelConfig(); // 현재 화면 크기 기준으로 반환
    }
  });

  // 드래그 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragPanel, setDragPanel] = useState(null);

  // localStorage에 설정 저장
  const saveToStorage = useCallback((config) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
    }
  }, []);

  // 패널 토글
  const togglePanel = useCallback((panelName) => {
    // 폼 패널은 강제 숨김
    if (FORCE_HIDE_FORMS_PANEL && panelName === 'forms') {
      return;
    }

    setPanelConfig(prev => {
      const newConfig = {
        ...prev,
        [panelName]: {
          ...prev[panelName],
          visible: !prev[panelName].visible
        }
      };
      
      // 채팅창이 꺼질 때 대시보드 너비를 재계산
      if (panelName === 'chat' && prev[panelName].visible) {
        // 채팅창이 꺼지는 경우
        const screenWidth = window.innerWidth;
        const formsWidth = prev.forms.visible ? prev.forms.width : 0;
        const availableWidth = screenWidth - formsWidth - 20; // 20px 여백
        
        newConfig.dashboard = {
          ...newConfig.dashboard,
          width: availableWidth
        };
      } else if (panelName === 'chat' && !prev[panelName].visible) {
        // 채팅창이 켜지는 경우 - 기본 비율로 복원
        const screenWidth = window.innerWidth;
        const dashboardWidth = Math.floor(screenWidth * 0.75);
        
        newConfig.dashboard = {
          ...newConfig.dashboard,
          width: dashboardWidth
        };
      }
      
      saveToStorage(newConfig);
      return newConfig;
    });
  }, [saveToStorage]);

  // 패널 크기 조정
  const resizePanel = useCallback((panelName, width) => {
    setPanelConfig(prev => {
      const panel = prev[panelName];
      const clampedWidth = Math.max(
        panel.minWidth,
        Math.min(panel.maxWidth, width)
      );
      
      const newConfig = {
        ...prev,
        [panelName]: {
          ...prev[panelName],
          width: clampedWidth
        }
      };
      
      saveToStorage(newConfig);
      return newConfig;
    });
  }, [saveToStorage]);

  // 드래그 시작
  const startDrag = useCallback((panelName) => {
    setIsDragging(true);
    setDragPanel(panelName);
  }, []);

  // 드래그 종료
  const endDrag = useCallback(() => {
    setIsDragging(false);
    setDragPanel(null);
  }, []);

  // 설정 초기화
  const resetToDefault = useCallback(() => {
    const currentDefaultConfig = calculateDefaultPanelConfig();
    setPanelConfig(currentDefaultConfig);
    saveToStorage(currentDefaultConfig);
  }, [saveToStorage]);

  // 키보드 단축키 처리
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey) {
        switch (e.key) {
          case 'E':
            e.preventDefault();
            togglePanel('dashboard');
            break;
          case 'F':
            if (!FORCE_HIDE_FORMS_PANEL) {
              e.preventDefault();
              togglePanel('forms');
            }
            break;
          default:
            break;
        }
      } else if (e.ctrlKey && e.key === '`') {
        e.preventDefault();
        togglePanel('chat');
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false }); // preventDefault를 사용하므로 passive: false
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePanel]);

  return {
    panelConfig,
    isDragging,
    dragPanel,
    togglePanel,
    resizePanel,
    startDrag,
    endDrag,
    resetToDefault
  };
}; 